import { mergeCustomDimensions } from '../configurable-step/applyProductContext';
import {
  createElementClickedPayload,
  createElementLoadedPayload,
  createElementViewedPayload,
  createProductClickedPayload,
  createProductLoadedPayload,
  createProductViewedPayload,
  type EventContext,
} from '../createEventingPayloadsDE';
import { FORBIDDEN_KEYS } from '../interpolation/forbiddenKeys';
import { getByPath } from '../interpolation/getByPath';
import { normalizeCustomDimensions } from '../normalizeCustomDimensions';
import type { CustomDimensions } from '../../types/types';
import type {
  BindingExperience,
  CatalogEventEntry,
  CatalogField,
  CatalogFieldType,
  EventBinding,
} from './types';

export type ResolvedWireValue = string | number | boolean;

export type ComposeDropReason = 'unsupported-event' | 'required-missing';

export type ComposedConfiguredEvent =
  | { readonly name: string; readonly payload: Record<string, unknown> }
  | { readonly reason: ComposeDropReason };

export interface ComposeConfiguredEventInput {
  readonly binding: EventBinding;
  readonly entry: CatalogEventEntry;
  readonly resolvedFields: Readonly<
    Record<string, ResolvedWireValue | undefined>
  >;
  readonly resolvedCustomDimensions?: Readonly<
    Record<string, ResolvedWireValue | undefined>
  >;
  readonly experience: BindingExperience;
  readonly eventContext: EventContext;
  readonly valueSets?: Readonly<
    Record<string, { readonly values: readonly string[] }>
  >;
}

function wireNames(path: string): string[] | undefined {
  if (
    path.length === 0 ||
    path.includes('$') ||
    path.includes('*') ||
    path.includes('[') ||
    path.includes(']')
  ) {
    return undefined;
  }
  const names = path.split('.');
  if (
    names.some(
      (name) => name.trim().length === 0 || FORBIDDEN_KEYS.has(name.trim()),
    )
  ) {
    return undefined;
  }
  return names.map((name) => name.trim());
}

function readWire(root: unknown, path: string): unknown {
  const names = wireNames(path);
  if (!names) return undefined;
  return getByPath(
    root,
    names.map((name) => ({ type: 'key', name })),
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function writeWire(
  root: Record<string, unknown>,
  path: string,
  value: ResolvedWireValue,
): void {
  const names = wireNames(path);
  if (!names || names.length === 0) return;
  let cursor = root;
  for (let index = 0; index < names.length - 1; index += 1) {
    const key = names[index];
    const next = cursor[key];
    if (!isRecord(next)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[names[names.length - 1]] = value;
}

function deleteWire(root: Record<string, unknown>, path: string): void {
  const names = wireNames(path);
  if (!names || names.length === 0) return;
  let cursor: unknown = root;
  for (let index = 0; index < names.length - 1; index += 1) {
    if (!isRecord(cursor)) return;
    cursor = cursor[names[index]];
  }
  if (!isRecord(cursor)) return;
  delete cursor[names[names.length - 1]];
}

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Catalog `type` from a resolver string or a raw scalar. An empty
 * string is a miss. A numeric string such as `"42"` is a number.
 * Boolean text is only `true` or `false`.
 */
export function coerceCatalogValue(
  type: CatalogFieldType,
  value: unknown,
): ResolvedWireValue | undefined {
  if (type === 'string') {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' && Number.isFinite(value))
      return String(value);
    if (typeof value === 'boolean') return String(value);
    return undefined;
  }
  if (type === 'number') {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return undefined;
  }
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

function remember(paths: string[], path: string): void {
  if (!paths.includes(path)) paths.push(path);
}

/**
 * Stamps replace an author dimension with the same key. Values are
 * strings, which is the shape Tagular accepts.
 */
export function applyConfiguredEventStamps(
  dimensions: CustomDimensions | undefined,
  stamps: CustomDimensions,
): CustomDimensions | undefined {
  return normalizeCustomDimensions(mergeCustomDimensions(dimensions, stamps));
}

function stampEntries(
  experience: BindingExperience,
  bindingId: string,
  fallbacks: readonly string[],
): CustomDimensions {
  const stamps: CustomDimensions = [];
  if (experience.dslConfigId) {
    stamps.push({ key: 'dslConfigId', value: experience.dslConfigId });
  }
  if (experience.dslConfigVersion !== undefined) {
    stamps.push({
      key: 'dslConfigVersion',
      value: String(experience.dslConfigVersion),
    });
  }
  if (experience.blockId) {
    stamps.push({ key: 'blockId', value: experience.blockId });
  }
  stamps.push({ key: 'eventBindingId', value: bindingId });
  if (fallbacks.length > 0) {
    stamps.push({ key: 'mappingFallback', value: fallbacks.join(',') });
  }
  return stamps;
}

/**
 * One payload builder per authorable catalog event, keyed by the
 * bound event name rather than `entry.family`. Family alone cannot
 * disambiguate Loaded / Viewed / Clicked within the same family —
 * each needs its own fields and its own platform correlation id
 * (e.g. `ElementLoaded`'s `correlationId` vs `ElementViewed`'s
 * `correlationId` + `viewCorrelationId`).
 */
const EVENT_BUILDERS: Readonly<
  Record<
    string,
    (context: EventContext) => {
      readonly name: string;
      readonly payload: Record<string, unknown>;
    }
  >
> = {
  ElementLoaded: createElementLoadedPayload,
  ElementViewed: createElementViewedPayload,
  ElementClicked: createElementClickedPayload,
  ProductLoaded: createProductLoadedPayload,
  ProductViewed: createProductViewedPayload,
  ProductClicked: createProductClickedPayload,
};

function outsideGovernedSet(
  field: CatalogField,
  value: ResolvedWireValue,
  valueSets: ComposeConfiguredEventInput['valueSets'],
): boolean {
  const valueSet = field.rules?.valueSet;
  if (!valueSet || !valueSets) return false;
  const allowed = valueSets[valueSet];
  if (!allowed) return false;
  return typeof value === 'string' && !allowed.values.includes(value);
}

/**
 * SAD composition for one configured click, mount, or view binding.
 * Sending stays with the caller. A drop returns a reason and does not
 * throw.
 *
 * The builder is selected by the bound event name (see
 * {@link EVENT_BUILDERS}), not `entry.family` — family only
 * distinguishes element vs. product, not which moment's payload shape
 * and platform correlation id apply. Platform paths are then copied
 * back from that builder's own output after author writes, so an
 * author value never stays on a platform field. The stamp is written
 * after the type check so `mappingFallback` includes a field the type
 * check removed.
 */
export function composeConfiguredEvent(
  input: ComposeConfiguredEventInput,
): ComposedConfiguredEvent {
  const { binding, entry, experience, eventContext } = input;
  const build = EVENT_BUILDERS[binding.event];
  if (!build) return { reason: 'unsupported-event' };

  const built = build(eventContext);
  const payload = JSON.parse(JSON.stringify(built.payload)) as Record<
    string,
    unknown
  >;
  const fields = entry.fields ?? {};
  const platformPaths = Object.keys(fields).filter(
    (path) => fields[path]?.rules?.platform === true,
  );
  const platformSnapshot: { path: string; value: unknown }[] = [];
  for (const path of platformPaths) {
    platformSnapshot.push({ path, value: readWire(payload, path) });
  }
  const fallbacks: string[] = [];

  for (const [path, field] of Object.entries(fields)) {
    if (field.rules?.platform === true) continue;
    if (field.rules?.default === undefined) continue;
    if (!isBlank(readWire(payload, path))) continue;
    const coerced = coerceCatalogValue(field.type, field.rules.default);
    if (coerced !== undefined) writeWire(payload, path, coerced);
  }

  const seenPath: Record<string, true> = Object.create(null);
  const boundPaths: string[] = [];
  for (const path of [
    ...Object.keys(binding.fields ?? {}),
    ...Object.keys(input.resolvedFields),
  ]) {
    if (seenPath[path]) continue;
    seenPath[path] = true;
    boundPaths.push(path);
  }
  for (const path of boundPaths) {
    const field = fields[path];
    if (!field || field.rules?.platform === true) continue;
    const resolved = input.resolvedFields[path];
    if (resolved === undefined || resolved === '') {
      remember(fallbacks, path);
      const fallback = field.rules?.default;
      const coerced =
        fallback === undefined
          ? undefined
          : coerceCatalogValue(field.type, fallback);
      if (coerced === undefined) deleteWire(payload, path);
      else writeWire(payload, path, coerced);
      continue;
    }
    const coerced = coerceCatalogValue(field.type, resolved);
    if (coerced === undefined) {
      remember(fallbacks, path);
      deleteWire(payload, path);
      continue;
    }
    if (
      outsideGovernedSet(field, coerced, input.valueSets) &&
      field.rules?.blocking === true
    ) {
      remember(fallbacks, path);
      deleteWire(payload, path);
      continue;
    }
    writeWire(payload, path, coerced);
  }

  for (const [path, field] of Object.entries(fields)) {
    if (field.rules?.platform === true) continue;
    const current = readWire(payload, path);
    if (isBlank(current)) continue;
    const coerced = coerceCatalogValue(field.type, current);
    if (coerced === undefined) {
      remember(fallbacks, path);
      deleteWire(payload, path);
      continue;
    }
    if (
      outsideGovernedSet(field, coerced, input.valueSets) &&
      field.rules?.blocking === true
    ) {
      remember(fallbacks, path);
      deleteWire(payload, path);
      continue;
    }
    writeWire(payload, path, coerced);
  }

  const authorDimensions: CustomDimensions = [];
  const missedDimensionKeys = new Set<string>();
  if (entry.acceptsCustomDimensions) {
    for (const [key, value] of Object.entries(
      input.resolvedCustomDimensions ?? {},
    )) {
      if (FORBIDDEN_KEYS.has(key)) continue;
      if (value === undefined || value === '') {
        remember(fallbacks, key);
        missedDimensionKeys.add(key);
        continue;
      }
      authorDimensions.push({ key, value: String(value) });
    }
  }
  const builderDimensions = Array.isArray(payload.customDimensions)
    ? (payload.customDimensions as CustomDimensions).filter(
        (dimension) =>
          !missedDimensionKeys.has(dimension.key) &&
          // The product builder names productFields misses here. A
          // configured event names only its own misses, added below.
          dimension.key !== 'mappingFallback',
      )
    : undefined;
  // `ElementLoaded`'s schema (core.ElementLoaded.v1) has no
  // `customDimensions` property — Tagular ingestion rejects it on the
  // wire (see createFieldEventsPayload's field-event note for the
  // same constraint). Stamps are binding/author provenance, not part
  // of the event's own contract, so an event whose schema doesn't
  // accept custom dimensions gets none at all rather than a partial
  // or malformed set.
  if (entry.acceptsCustomDimensions) {
    const stamped = applyConfiguredEventStamps(
      mergeCustomDimensions(builderDimensions, authorDimensions),
      stampEntries(experience, binding.id, fallbacks),
    );
    if (stamped) payload.customDimensions = stamped;
    else delete payload.customDimensions;
  } else {
    delete payload.customDimensions;
  }

  for (const snapshot of platformSnapshot) {
    const { path, value } = snapshot;
    const field = fields[path];
    if (value === undefined || value === null) {
      deleteWire(payload, path);
      continue;
    }
    const coerced = field ? coerceCatalogValue(field.type, value) : undefined;
    if (coerced === undefined) deleteWire(payload, path);
    else writeWire(payload, path, coerced);
  }

  if (requiredFieldMissing(fields, payload)) {
    return { reason: 'required-missing' };
  }

  return { name: built.name, payload };
}

function requiredFieldMissing(
  fields: Readonly<Record<string, CatalogField>>,
  payload: Record<string, unknown>,
): boolean {
  for (const [path, field] of Object.entries(fields)) {
    if (field.rules?.required !== true) continue;
    if (isBlank(readWire(payload, path))) return true;
  }
  return false;
}
